import {
  div,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  header,
  p,
  type ChildNode,
  type Mountable,
} from '@llui/dom';

/**
 * `pageHeader(...)` + `sectionHeading(...)` — the recurring page/section
 * header chrome (per `30-design-system.md §8`).
 *
 * `pageHeader` is the surface-top title block: a default sans page title, an
 * optional muted intro line, and an optional right-aligned actions slot
 * (the "New entity" button, a filter toggle, …). It renders a `<header>`
 * landmark with an `<h1>` — use ONE per route.
 *
 * `sectionHeading` is the in-page section label: a heading at a
 * caller-chosen level (`h2`–`h6`) plus an optional actions slot. The
 * level is configurable precisely so `heading-order` stays valid — the
 * a11y gate (axe) checks heading order, so a section under the page's
 * `<h1>` passes `level: 2`, a sub-section `level: 3`, etc. There is NO
 * default — the caller MUST think about the level (the option is
 * required) so the rewire can't silently produce an h-order violation.
 *
 * UNREVIEWED (Phase A): minimal baseline; flagged in `/ui-gallery`.
 */

export interface PageHeaderOptions {
  /** Page title (default sans, rendered as the route's `<h1>`). */
  title: ChildNode;
  /** Optional muted intro / subtitle line. */
  intro?: ChildNode;
  /** Optional right-aligned actions slot (buttons, toggles). */
  actions?: Mountable;
  /** Optional `id` on the `<h1>` (for the route's `aria-labelledby`). */
  titleId?: string;
  /** Optional `data-testid` on the `<h1>`. */
  titleTestId?: string;
}

export function pageHeader(opts: PageHeaderOptions): Mountable {
  const titleBlock: Mountable[] = [
    // <h1> — the route owns exactly one. Use ONE pageHeader per route.
    h1(
      {
        class: 'dl-page-header-title',
        ...(opts.titleId !== undefined ? { id: opts.titleId } : {}),
        ...(opts.titleTestId !== undefined ? { 'data-testid': opts.titleTestId } : {}),
      },
      [opts.title],
    ),
  ];
  if (opts.intro !== undefined) {
    titleBlock.push(p({ class: 'dl-page-header-intro' }, [opts.intro]));
  }

  const children: Mountable[] = [div({ class: 'dl-page-header-titles' }, titleBlock)];
  if (opts.actions !== undefined) {
    children.push(div({ class: 'dl-page-header-actions' }, [opts.actions]));
  }

  return header({ class: 'dl-page-header' }, children);
}

/** Heading level for `sectionHeading` — 2..6 (the page's `<h1>` is owned
 * by `pageHeader`, so section headings never go above `h2`). */
export type HeadingLevel = 2 | 3 | 4 | 5 | 6;

/** Section-heading style: a default sans `title` (default, for card/section
 * titles) or an uppercase sans `overline` grouping rail. */
export type SectionHeadingVariant = 'title' | 'overline';

export interface SectionHeadingOptions {
  /** Heading text. */
  title: ChildNode;
  /** Heading level (2–6) — REQUIRED so `heading-order` stays valid. */
  level: HeadingLevel;
  /** Visual style — `title` (default sans, default) or `overline` (uppercase rail). */
  variant?: SectionHeadingVariant;
  /** Optional right-aligned actions slot. */
  actions?: Mountable;
  /** Optional `id` on the heading (for `aria-labelledby` wiring). */
  id?: string;
  /** Optional `data-testid` on the heading. */
  testId?: string;
}

const HEADING_HELPERS = { 2: h2, 3: h3, 4: h4, 5: h5, 6: h6 } as const;

export function sectionHeading(opts: SectionHeadingOptions): Mountable {
  const helper = HEADING_HELPERS[opts.level];
  const variant: SectionHeadingVariant = opts.variant ?? 'title';
  const titleClass =
    variant === 'overline'
      ? 'dl-section-heading-title dl-section-heading-title--overline'
      : 'dl-section-heading-title';
  const heading = helper(
    {
      class: titleClass,
      ...(opts.id !== undefined ? { id: opts.id } : {}),
      ...(opts.testId !== undefined ? { 'data-testid': opts.testId } : {}),
    },
    [opts.title],
  );

  const children: Mountable[] = [heading];
  if (opts.actions !== undefined) {
    children.push(div({ class: 'dl-section-heading-actions' }, [opts.actions]));
  }

  return div({ class: 'dl-section-heading' }, children);
}
